import { StudioCompareMenu } from 'src/components/star-grid/components/StudioCompareMenu';
import React from 'react';
import Button from '@amzn/awsui-components-core/button';
import Modal from '@amzn/awsui-components-core/modal';
import { StudioTextInput } from 'src/components/star-grid/components/StudioTextInput';
import FormField from '@amzn/awsui-components-core/form-field';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StudioColumn } from 'src/components/star-grid/reportStudio.types';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { useStudioColumn } from 'src/components/star-grid/hooks/useStudioColumn';
import { StudioSelect, StudioMultiselect } from 'src/components/star-grid/components/StudioControls';
import { StudioCompactSelect } from 'src/components/star-grid/components/StudioCompactSelect';
import { StudioVarianceEditor } from 'src/components/star-grid/components/StudioVarianceEditor';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
interface Props {
  readonly column: StudioColumn;
  readonly index: number;
  readonly state: ReportStudioState;
}
export const StudioColumnEditor: React.FC<Props> = ({ column, index, state }) => {
  const edit = useStudioColumn(column, state);
  const text = state.text;
  return (
    <article className={`${styles.columnCard} ${column.kind === 'variance' ? styles.varianceCard : ''}`} data-testid="studio-column">
      <header className={styles.blockHeader}>
        {column.kind === 'scenario' ? (
          <StudioCompactSelect
            label={text('studioScenario')}
            value={column.scenario_id}
            options={state.scenarios}
            onChange={(scenario_id) =>
              state.updateColumn({
                ...column,
                scenario_id,
                label: state.template.catalog.scenarios.find((item) => item.id === scenario_id)?.label ?? scenario_id
              })
            }
          />
        ) : (
          <strong>{column.label || text(column.kind === 'variance' ? 'studioVariance' : 'studioAttribute')}</strong>
        )}
        <span className={styles.spacer} />
        <Button variant="icon" iconName="close" ariaLabel={text('studioRemoveColumn')} onClick={() => state.removeColumn(column.id)} />
      </header>
      <div className={styles.blockBody}>
        {column.kind === 'scenario' && (
          <>
            <div className={styles.periodChips} ref={edit.periodsRef} data-testid="studio-periods">
              {state.template.catalog.periods.map((period) => (
                <button
                  key={period.id}
                  data-period-id={period.id}
                  type="button"
                  className={`${styles.periodChip} ${column.period_ids.includes(period.id) ? styles.selected : ''} ${
                    period.months.length > 1 ? styles.aggregate : ''
                  }`}
                  aria-pressed={column.period_ids.includes(period.id)}
                  onClick={() => edit.togglePeriod(period.id)}
                >
                  {period.label}
                </button>
              ))}
            </div>
            <div className={styles.fxRow}>
              <span>{text('studioFX')}</span>
              <StudioCompactSelect
                label={text('studioFX')}
                value={column.fx_policy_id ?? ''}
                options={[{ id: '', label: text('studioDefaultFX') }, ...state.dataset.fx_policies]}
                onChange={(fx_policy_id) => state.updateColumn({ ...column, fx_policy_id: fx_policy_id || null })}
              />
            </div>
            <div className={styles.fxRow}>
              <span>{text('studioVersion')}</span>
              <StudioCompactSelect
                label={text('studioVersion')}
                value={column.version_id ?? ''}
                options={[{ id: '', label: text('studioReportVersion') }, ...state.template.catalog.versions]}
                onChange={(version_id) => state.updateColumn({ ...column, version_id: version_id || null })}
              />
            </div>
          </>
        )}
        {column.kind === 'variance' && <StudioVarianceEditor compact column={column} state={state} edit={edit} />}
        {column.kind === 'attribute' && (
          <SpaceBetween size="xs">
            <StudioSelect
              label={text('studioRowInstance')}
              value={column.row_instance_id}
              options={state.deck.rows.map((row, position) => ({
                id: row.id,
                label: `${position + 1}. ${state.dataset.bindings.find((item) => item.id === row.binding_id)?.label ?? ''}`
              }))}
              onChange={(row_instance_id) => state.updateColumn({ ...column, row_instance_id, attribute_id: '' })}
            />
            <StudioSelect
              label={text('studioAttribute')}
              value={column.attribute_id}
              options={
                state.dataset.bindings.find((binding) => binding.id === state.deck.rows.find((row) => row.id === column.row_instance_id)?.binding_id)
                  ?.attributes ?? []
              }
              onChange={(attribute_id) => state.updateColumn({ ...column, attribute_id })}
            />
          </SpaceBetween>
        )}
      </div>
      <footer className={styles.blockFooter}>
        <Button variant="inline-link" onClick={() => edit.setOpen(true)}>
          {text('studioEdit')}
        </Button>
        {column.kind === 'scenario' && (
          <StudioCompareMenu
            testId={`studio-compare-${column.id}`}
            label={text('studioCompareTo')}
            closeLabel={text('studioClose')}
            options={state.scenarios}
            onSelect={(id) => state.addScenario(id, column.id)}
          />
        )}
        <span className={styles.spacer} />
        <Button
          variant="icon"
          iconName="angle-left"
          ariaLabel={text('studioMoveLeft')}
          disabled={index === 0}
          onClick={() => state.moveColumn(index, -1)}
        />
        <Button
          variant="icon"
          iconName="angle-right"
          ariaLabel={text('studioMoveRight')}
          disabled={index === state.deck.columns.length - 1}
          onClick={() => state.moveColumn(index, 1)}
        />
      </footer>
      <Modal
        visible={edit.open}
        header={text('studioColumnSettings')}
        onDismiss={() => edit.setOpen(false)}
        closeAriaLabel={text('studioClose')}
        footer={<Button onClick={() => edit.setOpen(false)}>{text('studioDone')}</Button>}
      >
        <SpaceBetween size="m">
          <FormField label={text('studioLabel')}>
            <StudioTextInput value={column.label} onCommit={(label) => state.updateColumn({ ...column, label })} />
          </FormField>
          {column.kind === 'variance' && <StudioVarianceEditor column={column} state={state} edit={edit} />}
          {column.kind === 'scenario' && (
            <>
              <StudioSelect
                label={text('studioMeasure')}
                value={column.metric_id}
                options={state.dataset.metrics}
                onChange={(metric_id) => state.updateColumn({ ...column, metric_id })}
              />
              <StudioMultiselect
                label={text('studioCurrencies')}
                values={column.currency_ids}
                options={state.dataset.currencies}
                onChange={(currency_ids) => state.updateColumn({ ...column, currency_ids })}
              />
            </>
          )}
        </SpaceBetween>
      </Modal>
    </article>
  );
};
